import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'

import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'

import { ENVIRONMENT } from '../tokens/environment.token'

// Token refresh lock mechanism to prevent multiple simultaneous refresh requests
let isRefreshing = false
let refreshSubject = new BehaviorSubject<boolean>(false)

/**
 * An HTTP interceptor that handles authentication via HttpOnly cookies.
 *
 * - Adds withCredentials to all API requests so cookies are sent automatically
 * - Handles 401 errors by triggering a token refresh
 * - Uses a lock mechanism to ensure only one refresh happens at a time
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const env = inject(ENVIRONMENT)

  const isYoshuApiRequest = req.url.startsWith(env.apiUrl)

  if (!isYoshuApiRequest) {
    return next(req)
  }

  // Add withCredentials and X-Tenant-ID header to all API requests.
  // In dev with a service API key, also send Bearer auth — the backend's
  // ServiceAPIKeyAuthentication identifies a service user, bypassing cookies.
  const headers: Record<string, string> = {}
  if (env.tenantId) headers['X-Tenant-ID'] = env.tenantId
  if (env.serviceApiKey) headers['Authorization'] = `Bearer ${env.serviceApiKey}`

  const apiReq = req.clone({
    withCredentials: true,
    ...(Object.keys(headers).length ? { setHeaders: headers } : {})
  })

  // With a service API key there is no refresh token; skip the 401 dance.
  const isAuthRequest = req.url.includes('/auth/')
  if (isAuthRequest || env.serviceApiKey) {
    return next(apiReq)
  }

  return next(apiReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handleTokenRefresh(req, next, authService)
      }
      return throwError(() => error)
    })
  )
}

/**
 * Handles token refresh with a lock mechanism to prevent race conditions.
 * If a refresh is already in progress, waits for it to complete.
 */
function handleTokenRefresh(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService
) {
  if (isRefreshing) {
    // Wait for the ongoing refresh to complete
    return refreshSubject.pipe(
      filter((refreshed) => refreshed),
      take(1),
      switchMap(() => {
        // Retry the original request with credentials
        const retriedReq = req.clone({ withCredentials: true })
        return next(retriedReq)
      })
    )
  }

  // Start a new refresh
  isRefreshing = true
  refreshSubject.next(false)

  return authService.refreshAccessToken().pipe(
    switchMap(() => {
      isRefreshing = false
      refreshSubject.next(true)

      // Retry the original request - new access token cookie is already set by backend
      const retriedReq = req.clone({ withCredentials: true })
      return next(retriedReq)
    }),
    catchError((refreshError) => {
      isRefreshing = false
      // Signal failure to all waiting requests so their observables error out
      // instead of hanging forever on the filter(v => v) gate.
      refreshSubject.error(refreshError)
      refreshSubject = new BehaviorSubject<boolean>(false)

      authService.endSession()

      return throwError(() => refreshError)
    })
  )
}
